import { getAccessToken } from '../../../../Services/getAccessToken';
import { ENVIRONMENT_URL, RAJPURA_TABLES, getRajpuraTourId, sanitizeParentTourPayload } from '../../../../constants/dataverse';
import {
    STORES,
    putToStore,
    getAllFromStore,
    getFromStore,
    enqueueSync,
    getCCPOPRPCheckpointsForTour,
    getSievesMagnetsCheckpointsForTour
} from '../../../../Services/offlineDB';
import type { CCPSessionData, CCPOPRPCheckpointRow, SievesMagnetsItemRow,  } from '../context/CCPOPRPSievesContext';

const TABLES = {
    QUALITY_TOUR: RAJPURA_TABLES.QUALITY_TOUR,
    CCP_OPRP_CHECKS: RAJPURA_TABLES.CCP_OPRP,
    SIEVES_MAGNETS: RAJPURA_TABLES.SIEVES_MAGNETS
};

const CONFIG_CACHE_KEY = "mrs_bectors_ccp_oprp_sieves_config";

export const ccpOprpSievesService = {
    /**
     * Fetch QA / Production Personnel Config from SharePoint or Local Cache
     */
    getConfig: async function (accessToken?: string) {
        // 1. Try fetching from SharePoint if online and token available
        if (navigator.onLine && accessToken) {
            const listNames = ["Quality-Rajpura-CCPOPRP", "Quality-Rajpura", "Quality-Rajpura-FoodSafety"];
            const { buildSharePointListItemsUrl, SP_SITE_URL } = await import('../../../../constants/sharepoint');

            for (const listName of listNames) {
                try {
                    const fieldsUrl = `${SP_SITE_URL}/_api/web/lists/getByTitle('${listName}')/Fields?$select=InternalName,Title,TypeAsString`;
                    const fieldsResponse = await fetch(fieldsUrl, {
                        headers: { "Accept": "application/json;odata=verbose", "Authorization": `Bearer ${accessToken}` }
                    });

                    if (fieldsResponse.ok) {
                        const fieldsData = await fieldsResponse.json();
                        const listFields = fieldsData.d?.results || [];

                        const findField = (possibleNames: string[], displayName: string) => {
                            let match = listFields.find((f: any) => possibleNames.includes(f.InternalName));
                            if (match) return match;
                            return listFields.find((f: any) => f.Title === displayName);
                        };

                        const configTypeField = findField(["ConfigType", "Config_x0020_Type"], "Config Type");
                        const plantField = findField(["Plant"], "Plant");
                        const assignedQAField = findField(["AssignedQA", "Assigned_x0020_QA", "AssignedUser", "Assigned_x0020_User", "QAExecutive"], "Assigned QA");
                        const escalationManagerField = findField(["EscalationManager", "Escalation_x0020_Manager"], "Escalation Manager");
                        const productionInchargeField = findField(["ProductionIncharge", "Production_x0020_Incharge"], "Production Incharge");
                        const lineNameField = findField(["LineName", "Line_x0020_Name", "Line"], "Line Name");
                        const productCodeField = findField(["ProductCode", "Product_x0020_Code", "SKU"], "Product Code");
                        const productCategoryField = findField(["ProductCategory", "Product_x0020_Category", "Category"], "Product Category");
                        const isActiveField = findField(["IsActive", "Is_x0020_Active", "Active"], "Is Active");

                        const selectParts = ["Id", "Title"];
                        const expandParts: string[] = [];

                        if (plantField) selectParts.push(plantField.InternalName);
                        if (configTypeField) selectParts.push(configTypeField.InternalName);
                        if (lineNameField) selectParts.push(lineNameField.InternalName);
                        if (productCodeField) selectParts.push(productCodeField.InternalName);
                        if (productCategoryField) selectParts.push(productCategoryField.InternalName);
                        if (isActiveField) selectParts.push(isActiveField.InternalName);

                        if (assignedQAField) {
                            const name = assignedQAField.InternalName;
                            selectParts.push(`${name}/Title`, `${name}/EMail`, `${name}/Id`);
                            expandParts.push(name);
                        }
                        if (escalationManagerField) {
                            const name = escalationManagerField.InternalName;
                            selectParts.push(`${name}/Title`, `${name}/EMail`, `${name}/Id`);
                            expandParts.push(name);
                        }
                        if (productionInchargeField) {
                            const name = productionInchargeField.InternalName;
                            selectParts.push(`${name}/Title`, `${name}/EMail`, `${name}/Id`);
                            expandParts.push(name);
                        }

                        let query = `$select=${selectParts.join(",")}`;
                        if (expandParts.length > 0) {
                            query += `&$expand=${expandParts.join(",")}`;
                        }

                        const itemsUrl = buildSharePointListItemsUrl(listName, query);
                        const itemsResponse = await fetch(itemsUrl, {
                            headers: { "Accept": "application/json;odata=verbose", "Authorization": `Bearer ${accessToken}` }
                        });

                        if (itemsResponse.ok) {
                            const itemsData = await itemsResponse.json();
                            const results = itemsData.d?.results || [];

                            if (results.length > 0) {
                                const parsed = results.map((item: any) => {
                                    const qaFieldKey = assignedQAField?.InternalName;
                                    const prodFieldKey = productionInchargeField?.InternalName;
                                    const rawQA = qaFieldKey ? item[qaFieldKey] : (item.AssignedQA || item.AssignedUser || item.QAExecutive);
                                    const rawProd = prodFieldKey ? item[prodFieldKey] : (item.ProductionIncharge || item.Production_x0020_Incharge);

                                    let qaList: any[] = [];
                                    if (rawQA && rawQA.results && Array.isArray(rawQA.results)) {
                                        qaList = rawQA.results.map((u: any) => ({ Title: u.Title, EMail: u.EMail, Id: u.Id }));
                                    } else if (rawQA && rawQA.Title) {
                                        qaList = [{ Title: rawQA.Title, EMail: rawQA.EMail, Id: rawQA.Id }];
                                    }

                                    let prodList: any[] = [];
                                    if (rawProd && rawProd.results && Array.isArray(rawProd.results)) {
                                        prodList = rawProd.results.map((u: any) => ({ Title: u.Title, EMail: u.EMail, Id: u.Id }));
                                    } else if (rawProd && rawProd.Title) {
                                        prodList = [{ Title: rawProd.Title, EMail: rawProd.EMail, Id: rawProd.Id }];
                                    }

                                    const configTypeVal = configTypeField ? item[configTypeField.InternalName] : (item.ConfigType || item.Config_x0020_Type || item.ChecklistType || "");

                                    return {
                                        Id: item.Id,
                                        Title: item.Title,
                                        Plant: plantField ? item[plantField.InternalName] : (item.Plant || "Rajpura"),
                                        ConfigType: configTypeVal,
                                        ChecklistType: configTypeVal,
                                        LineName: lineNameField ? item[lineNameField.InternalName] : (item.LineName || item.Line_x0020_Name || "All Lines"),
                                        ProductCode: productCodeField ? item[productCodeField.InternalName] : (item.ProductCode || item.Product_x0020_Code || ""),
                                        ProductCategory: productCategoryField ? item[productCategoryField.InternalName] : (item.ProductCategory || item.Product_x0020_Category || ""),
                                        IsActive: isActiveField ? (item[isActiveField.InternalName] !== false) : (item.IsActive !== false),
                                        QAExecutives: qaList,
                                        ProductionIncharges: prodList
                                    };
                                });

                                if (parsed.length > 0) {
                                    try {
                                        localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(parsed));
                                        await putToStore(STORES.CONFIG, { id: 'ccp_oprp_sieves_config', data: parsed, updatedAt: Date.now() });
                                    } catch (cacheErr) {
                                        console.warn("Could not cache config:", cacheErr);
                                    }
                                    return parsed;
                                }
                            }
                        }
                    }
                } catch (e) {
                    console.warn(`Could not dynamically probe SharePoint list ${listName}:`, e);
                }
            }
        }

        // 2. Load from IndexedDB
        try {
            const idbEntry = await getFromStore(STORES.CONFIG, 'ccp_oprp_sieves_config');
            if (idbEntry && idbEntry.data && Array.isArray(idbEntry.data) && idbEntry.data.length > 0) {
                return idbEntry.data;
            }
        } catch (idbErr) {
            console.warn("Could not read config from IndexedDB:", idbErr);
        }

        // 3. Fallback to localStorage (including FoodSafety and ALC caches)
        try {
            const cachedKeys = [CONFIG_CACHE_KEY, "food_safety_config", "alc_qa_config_cache"];
            for (const key of cachedKeys) {
                const cached = localStorage.getItem(key);
                if (cached) {
                    const parsed = JSON.parse(cached);
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        return parsed;
                    }
                }
            }
        } catch (storageErr) {
            console.warn("Error reading config cache:", storageErr);
        }

        // 4. Default Personnel and Product Master matching SharePoint list Quality-Rajpura-CCPOPRP
        const seedProducts = [
            { Title: "Speciality Sauces", ProductCode: "CCP-ALL-001", ProductCategory: "Sauces & Condiments" },
            { Title: "Zesty Wasabi", ProductCode: "CCP-ALL-002", ProductCategory: "Sauces & Condiments" },
            { Title: "Mayonnaise", ProductCode: "CCP-ALL-003", ProductCategory: "Sauces & Condiments" },
            { Title: "Sandwich Spread", ProductCode: "CCP-ALL-004", ProductCategory: "Sauces & Condiments" },
            { Title: "Indian Chutneys", ProductCode: "CCP-ALL-005", ProductCategory: "Sauces & Condiments" },
            { Title: "Chocochip Cookies", ProductCode: "CCP-ALL-006", ProductCategory: "Wirecut" },
            { Title: "Krack Bite Crackers", ProductCode: "CCP-ALL-007", ProductCategory: "Cracker" },
            { Title: "Premium Butter Cookies", ProductCode: "CCP-ALL-008", ProductCategory: "Moulded Cookies" },
            { Title: "Bourbon Cream Biscuits", ProductCode: "CCP-ALL-009", ProductCategory: "Sandwich cream cookies" },
            { Title: "Spheres Cream Biscuits", ProductCode: "CCP-ALL-010", ProductCategory: "Sandwich cream cookies" },
            { Title: "Digestive & DBC Tins", ProductCode: "CCP-ALL-011", ProductCategory: "DBC" },
            { Title: "Marie Classic", ProductCode: "CCP-ALL-012", ProductCategory: "Moulded" },
            { Title: "Glucose Biscuits", ProductCode: "CCP-ALL-013", ProductCategory: "General" }
        ].map((p, idx) => ({
            Id: 100 + idx,
            Title: p.Title,
            Plant: "Rajpura",
            ConfigType: "Product Master",
            ChecklistType: "Product Master",
            LineName: "All Lines",
            ProductCode: p.ProductCode,
            ProductCategory: p.ProductCategory,
            IsActive: true,
            QAExecutives: [],
            ProductionIncharges: []
        }));

        return [
            {
                Id: 1,
                Title: "Line-1",
                Plant: "Rajpura",
                ConfigType: "CCP_OPRP",
                ChecklistType: "CCP_OPRP",
                LineName: "Line-1",
                QAExecutives: [
                    { Title: "Mishab Muhammed", EMail: "mishab@bectors.com", Id: 1 },
                    { Title: "Shaan Arshaqu", EMail: "shaan@bectors.com", Id: 2 },
                    { Title: "Gokul K", EMail: "gokul@bectors.com", Id: 3 }
                ],
                ProductionIncharges: [
                    { Title: "Mishab Muhammed", EMail: "mishab@bectors.com", Id: 1 },
                    { Title: "Gokul K", EMail: "gokul@bectors.com", Id: 3 }
                ]
            },
            {
                Id: 2,
                Title: "Sieves & Magnets Line 1",
                Plant: "Rajpura",
                ConfigType: "Sieves_Magnets",
                ChecklistType: "Sieves_Magnets",
                LineName: "Line-1",
                QAExecutives: [
                    { Title: "Mishab Muhammed", EMail: "mishab@bectors.com", Id: 1 },
                    { Title: "Shaan Arshaqu", EMail: "shaan@bectors.com", Id: 2 },
                    { Title: "Gokul K", EMail: "gokul@bectors.com", Id: 3 }
                ],
                ProductionIncharges: [
                    { Title: "Mishab Muhammed", EMail: "mishab@bectors.com", Id: 1 },
                    { Title: "Gokul K", EMail: "gokul@bectors.com", Id: 3 }
                ]
            },
            ...seedProducts
        ];
    },

    /**
     * Get active products filtered by selected line matching dal.js getProducts()
     */
    getProducts: function (configs: any[], selectedLine?: string) {
        const norm = (str: any) => String(str || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const targetLine = norm(selectedLine);

        let products = (configs || []).filter(c => {
            const isProduct = c.ConfigType === "Product Master" || c.ChecklistType === "Product Master";
            if (!isProduct) return false;
            if (c.IsActive === false) return false;
            if (!targetLine) return true;

            const pLine = norm(c.LineName);
            return pLine === "alllines" || pLine === "all" || !pLine || pLine === targetLine || pLine.includes(targetLine) || targetLine.includes(pLine);
        });

        if (products.length === 0) {
            products = [
                "Speciality Sauces",
                "Zesty Wasabi",
                "Mayonnaise",
                "Sandwich Spread",
                "Indian Chutneys",
                "Chocochip Cookies",
                "Krack Bite Crackers",
                "Premium Butter Cookies",
                "Bourbon Cream Biscuits",
                "Spheres Cream Biscuits",
                "Digestive & DBC Tins",
                "Marie Classic",
                "Glucose Biscuits"
            ].map((t, idx) => ({ Id: 100 + idx, Title: t }));
        }

        return products.sort((a, b) => (a.Title || "").localeCompare(b.Title || ""));
    },

    /**
     * Save / Create / Update Parent Tour Session
     */
    saveSession: async function (sessionData: any): Promise<{ id: string; success: boolean }> {
        const tourId = getRajpuraTourId(sessionData);
        let method: 'POST' | 'PATCH' = (tourId && !sessionData._isNew) ? 'PATCH' : 'POST';
        const sessionPayload = { ...sessionData };
        delete sessionPayload._isNew;

        const saveOfflineFallback = async () => {
            console.warn("Offline: saving CCP/OPRP/Sieves session to IndexedDB", sessionPayload);
            const id = tourId || `offline-ccp-${Date.now()}`;
            sessionPayload.cr3ea_prod_rajpura_quality_tourid = id;
            sessionPayload.cr3ea_rajpura_quality_tourid = id;
            sessionPayload.cr3ea_prod_qualitytourid = id;

            try {
                await putToStore(STORES.CCP_OPRP_SESSIONS, { id, data: sessionPayload, syncStatus: 'pending', updatedAt: Date.now() });
                await enqueueSync({
                    id: `${Date.now()}_session_${id}`,
                    type: 'ccp_oprp_session',
                    method: method,
                    payload: sessionPayload,
                    createdAt: Date.now()
                });
            } catch (err) {
                console.error("Failed to save session to IndexedDB:", err);
            }
            return { id, success: true };
        };

        if (!navigator.onLine) {
            return saveOfflineFallback();
        }

        try {
            const tokenResult = await getAccessToken();
            const token = tokenResult?.token;
            if (!token) throw new Error("No token available");

            const tableCandidates = [
                RAJPURA_TABLES.QUALITY_TOUR
            ];
            const apiVersion = "9.2";
            let response: Response | null = null;
            let lastErrText = "";

            for (const tableName of tableCandidates) {
                const currentPayload = sanitizeParentTourPayload(sessionPayload);
                let url = `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}`;
                if (method === 'PATCH' && tourId) {
                    url += `(${tourId.replace(/[{}]/g, "")})`;
                }

                delete currentPayload.cr3ea_prod_rajpura_quality_tourid;
                delete currentPayload.cr3ea_rajpura_quality_tourid;
                delete currentPayload.cr3ea_prod_qualitytourid;
                delete currentPayload.cr3ea_qualitytourid;
                delete currentPayload.id;

                try {
                    const res = await fetch(url, {
                        method: method,
                        headers: {
                            "Accept": "application/json",
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`,
                            "Prefer": "return=representation"
                        },
                        body: JSON.stringify(currentPayload)
                    });
                    if (res.ok) {
                        response = res;
                        break;
                    } else {
                        lastErrText = await res.text();
                        console.warn(`Dataverse CCP session save to '${tableName}' failed (${res.status}):`, lastErrText);
                    }
                } catch (e) {
                    lastErrText = String(e);
                }
            }

            if (!response || !response.ok) {
                console.warn(`Dataverse tour save failed: ${lastErrText}. Storing offline.`);
                return saveOfflineFallback();
            }

            let createdId = tourId;
            if (method === 'POST') {
                const entityHeader = response.headers.get("OData-EntityId");
                if (entityHeader) {
                    const match = entityHeader.match(/\(([^)]+)\)/);
                    if (match) createdId = match[1];
                } else {
                    const resData = await response.json();
                    createdId = getRajpuraTourId(resData);
                }
            }
            if (createdId) {
                sessionPayload.cr3ea_prod_rajpura_quality_tourid = createdId;
                sessionPayload.cr3ea_rajpura_quality_tourid = createdId;
                sessionPayload.cr3ea_prod_qualitytourid = createdId;
                await putToStore(STORES.CCP_OPRP_SESSIONS, { id: createdId, data: sessionPayload, syncStatus: 'synced', updatedAt: Date.now() });
            }
            return { id: createdId || tourId, success: true };
        } catch (e) {
            console.warn("Error calling Dataverse session API, falling back to offline IndexedDB:", e);
            return saveOfflineFallback();
        }
    },

    /**
     * Save CCP & OPRP Cycle Checkpoints
     */
    saveCCPOPRPCycle: async function (
        tourId: string,
        cycleName: string,
        checkpoints: CCPOPRPCheckpointRow[],
        session: CCPSessionData,
        acceptanceResponse: "OK" | "Line Not Operational" | "Plant Not Operational" = "OK",
        acceptanceReason?: string,
        isPause: boolean = false
    ): Promise<boolean> {
        const cleanTourId = tourId.replace(/[{}]/g, "");
        const cycleDate = new Date().toISOString();

        let rowsToSave: any[] = [];

        if (acceptanceResponse !== "OK" && checkpoints.length === 0) {
            rowsToSave = [
                {
                    cr3ea_title: `OPRP_CCP_${session.site}_${session.line}_${cycleName}_SHUTDOWN`,
                    cr3ea_cycle: cycleName,
                    cr3ea_shift: session.shift || "Shift 1",
                    cr3ea_tourstartdate: cycleDate,
                    cr3ea_observedby: session.qaExecutive || "",
                    cr3ea_batchno: session.batchNo || "",
                    cr3ea_location: session.line || "Line-1",
                    cr3ea_productname: session.productVariety || "",
                    cr3ea_category: "CCP",
                    cr3ea_checkpointname: "Shutdown Closure",
                    cr3ea_checkpointdescription: `Shutdown - ${acceptanceReason || acceptanceResponse}`,
                    cr3ea_criteria: "Okay",
                    cr3ea_defectremarks: acceptanceReason || acceptanceResponse,
                    cr3ea_acceptanceresponse: acceptanceResponse,
                    cr3ea_actiontaken: "",
                    cr3ea_notifieddepartment: "",
                    cr3ea_deviationstatus: "None",
                    cr3ea_escalated: "No",
                    _tourId: cleanTourId
                }
            ];
        } else if (isPause && checkpoints.length === 0) {
            rowsToSave = [
                {
                    cr3ea_title: `OPRP_CCP_${session.site}_${session.line}_${cycleName}_INIT`,
                    cr3ea_cycle: cycleName,
                    cr3ea_shift: session.shift || "Shift 1",
                    cr3ea_tourstartdate: cycleDate,
                    cr3ea_observedby: session.qaExecutive || "",
                    cr3ea_batchno: session.batchNo || "",
                    cr3ea_location: session.line || "Line-1",
                    cr3ea_productname: session.productVariety || "",
                    cr3ea_category: "CCP",
                    cr3ea_checkpointname: "Metadata Initialization",
                    cr3ea_checkpointdescription: "Session draft paused",
                    cr3ea_criteria: "Okay",
                    cr3ea_defectremarks: "",
                    cr3ea_acceptanceresponse: "In Progress",
                    cr3ea_actiontaken: "",
                    cr3ea_notifieddepartment: "",
                    cr3ea_deviationstatus: "None",
                    cr3ea_escalated: "No",
                    _tourId: cleanTourId
                }
            ];
        } else {
            rowsToSave = checkpoints.map((cp, idx) => {
                const title = `OPRP_CCP_${session.site}_${session.line}_${cycleName}_${idx + 1}`;
                return {
                    cr3ea_title: title,
                    cr3ea_cycle: cycleName,
                    cr3ea_shift: session.shift || "Shift 1",
                    cr3ea_tourstartdate: cycleDate,
                    cr3ea_observedby: session.qaExecutive || "",
                    cr3ea_batchno: session.batchNo || "",
                    cr3ea_location: session.line || "Line-1",
                    cr3ea_productname: session.productVariety || "",
                    cr3ea_category: cp.category || (cp.checkpointName.startsWith("CCP") ? "CCP" : "OPRP"),
                    cr3ea_checkpointname: cp.checkpointName,
                    cr3ea_checkpointdescription: cp.description,
                    cr3ea_criteria: cp.criteria || "Okay",
                    cr3ea_defectremarks: cp.defectRemarks || "",
                    cr3ea_acceptanceresponse: cp.checkpointName.includes("MD Sensitivity") 
                        ? `${cp.criteria === "Not Okay" ? "Not Okay" : "OK"} (${cp.mdSensitivityValue || "N/A"})`
                        : (cp.criteria === "Not Okay" ? "Not Okay" : "OK"),
                    cr3ea_actiontaken: cp.actionTaken || "",
                    cr3ea_notifieddepartment: cp.notifiedDepartment || (cp.criteria === "Not Okay" ? "Production" : ""),
                    cr3ea_deviationstatus: cp.deviationStatus || (cp.criteria === "Not Okay" ? "Raised" : "None"),
                    cr3ea_escalated: cp.escalated || "No",
                    _tourId: cleanTourId
                };
            });
        }

        // Always cache in IndexedDB
        for (const row of rowsToSave) {
            const id = `ccp_cp_${cleanTourId}_${cycleName}_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
            await putToStore(STORES.CCP_OPRP_CHECKPOINTS, {
                id,
                tourId: cleanTourId,
                cycle: cycleName,
                data: row,
                syncStatus: navigator.onLine ? 'pending' : 'offline',
                updatedAt: Date.now()
            });
        }

        if (!navigator.onLine) {
            await enqueueSync({
                id: `${Date.now()}_cycle_${cleanTourId}_${cycleName}`,
                type: 'ccp_oprp_cycle_checkpoints',
                tourId: cleanTourId,
                payload: rowsToSave,
                createdAt: Date.now()
            });
            return true;
        }

        // Try submitting to Dataverse
        try {
            const tokenResult = await getAccessToken();
            const token = tokenResult?.token;
            if (!token) return true;

            const apiVersion = "9.2";
            const tableCandidates = [TABLES.CCP_OPRP_CHECKS];

            for (const row of rowsToSave) {
                const payload: any = { ...row };
                delete payload._tourId;
                // Bind parent relationship
                payload[`cr3ea_qualitytourid@odata.bind`] = `/${TABLES.QUALITY_TOUR}(${cleanTourId})`;

                let success = false;
                for (const table of tableCandidates) {
                    try {
                        const res = await fetch(`${ENVIRONMENT_URL}/api/data/v${apiVersion}/${table}`, {
                            method: "POST",
                            headers: {
                                "Accept": "application/json",
                                "Content-Type": "application/json",
                                "Authorization": `Bearer ${token}`
                            },
                            body: JSON.stringify(payload)
                        });
                        if (res.ok) {
                            success = true;
                            break;
                        }
                    } catch (_) { }
                }

                if (!success) {
                    console.warn("Failed posting checkpoint row to Dataverse, queued for offline sync:", row);
                }
            }

            return true;
        } catch (e) {
            console.warn("Error posting CCP cycle to Dataverse:", e);
            return true;
        }
    },

    /**
     * Save Sieves & Magnets Cycle Checkpoints
     */
    saveSievesMagnetsCycle: async function (
        tourId: string,
        cycleName: string,
        items: SievesMagnetsItemRow[],
        session: CCPSessionData,
        frequency: "4hrs" | "8hrs"
    ): Promise<boolean> {
        const cleanTourId = tourId.replace(/[{}]/g, "");

        const rowsToSave = items.map((item, idx) => {
            const title = `Sieves_${session.site}_${cycleName}_${idx + 1}`;
            let remarks = item.defectRemarks || "";
            if (item.actionTaken && !remarks.includes(" | Action: ")) {
                remarks = remarks ? `${remarks} | Action: ${item.actionTaken}` : `Action: ${item.actionTaken}`;
            }

            return {
                cr3ea_title: title,
                cr3ea_cycle: cycleName,
                cr3ea_criteria: item.criteria || "Okay",
                cr3ea_description: item.description,
                cr3ea_defectremarks: remarks,
                cr3ea_frequency: frequency,
                _tourId: cleanTourId
            };
        });

        // Cache in IndexedDB
        for (const row of rowsToSave) {
            const id = `sieves_cp_${cleanTourId}_${cycleName}_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
            await putToStore(STORES.SIEVES_MAGNETS_CHECKPOINTS, {
                id,
                tourId: cleanTourId,
                cycle: cycleName,
                data: row,
                syncStatus: navigator.onLine ? 'pending' : 'offline',
                updatedAt: Date.now()
            });
        }

        if (!navigator.onLine) {
            await enqueueSync({
                id: `${Date.now()}_sieves_cycle_${cleanTourId}_${cycleName}`,
                type: 'sieves_magnets_cycle_checkpoints',
                tourId: cleanTourId,
                payload: rowsToSave,
                createdAt: Date.now()
            });
            return true;
        }

        // Try submitting to Dataverse
        try {
            const tokenResult = await getAccessToken();
            const token = tokenResult?.token;
            if (!token) return true;

            const apiVersion = "9.2";
            const tableCandidates = [TABLES.SIEVES_MAGNETS];

            for (const row of rowsToSave) {
                const payload: any = { ...row };
                delete payload._tourId;
                // Strip fields that do not exist on the Sieves entity to prevent Dataverse 400 Bad Request
                delete payload.cr3ea_actiontaken;
                delete payload.cr3ea_notifieddepartment;
                delete payload.cr3ea_deviationstatus;
                delete payload.cr3ea_checkpointname;
                delete payload.cr3ea_acceptanceresponse;
                delete payload.cr3ea_shift;
                delete payload.cr3ea_tourstartdate;
                delete payload.cr3ea_observedby;
                delete payload.cr3ea_location;
                delete payload.cr3ea_productname;
                delete payload.cr3ea_category;

                payload[`cr3ea_qualitytourid@odata.bind`] = `/${TABLES.QUALITY_TOUR}(${cleanTourId})`;

                let success = false;
                for (const table of tableCandidates) {
                    try {
                        const res = await fetch(`${ENVIRONMENT_URL}/api/data/v${apiVersion}/${table}`, {
                            method: "POST",
                            headers: {
                                "Accept": "application/json",
                                "Content-Type": "application/json",
                                "Authorization": `Bearer ${token}`
                            },
                            body: JSON.stringify(payload)
                        });
                        if (res.ok) {
                            success = true;
                            break;
                        }
                    } catch (_) { }
                }

                if (!success) {
                    console.warn("Failed posting Sieves row to Dataverse, queued for offline sync:", row);
                }
            }

            return true;
        } catch (e) {
            console.warn("Error posting Sieves cycle to Dataverse:", e);
            return true;
        }
    },

    /**
     * Get Checkpoints for a given tour (CCP/OPRP & Sieves)
     */
    getCCPOPRPCheckpoints: async function (tourId: string): Promise<any[]> {
        const cleanTourId = tourId.replace(/[{}]/g, "").trim().toLowerCase();
        let items: any[] = [];

        // 1. Fetch from Dataverse first if online
        if (navigator.onLine) {
            try {
                const tokenResult = await getAccessToken();
                const token = tokenResult?.token;
                if (token) {
                    const apiVersion = "9.2";
                    const query = `?$filter=_cr3ea_qualitytourid_value eq '${cleanTourId}'&$orderby=createdon asc`;
                    const tableCandidates = [
                        TABLES.CCP_OPRP_CHECKS
                    ];

                    for (const tbl of tableCandidates) {
                        try {
                            const res = await fetch(`${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tbl}${query}`, {
                                headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }
                            });
                            if (res.ok) {
                                const data = await res.json();
                                if (data.value && Array.isArray(data.value)) {
                                    items = data.value;
                                    break;
                                }
                            }
                        } catch (_) { }
                    }

                    if (items.length > 0) {
                        // Cache in IndexedDB
                        for (const row of items) {
                            const rowId = row.cr3ea_prod_rajpura_ccpoprpid || `ccp_cp_${cleanTourId}_${row.cr3ea_cycle}_${Math.random()}`;
                            try {
                                await putToStore(STORES.CCP_OPRP_CHECKPOINTS, {
                                    id: rowId,
                                    tourId: cleanTourId,
                                    cycle: row.cr3ea_cycle || "Cycle-1",
                                    data: row,
                                    syncStatus: 'synced',
                                    updatedAt: Date.now()
                                });
                            } catch (_) { }
                        }
                        return items;
                    }
                }
            } catch (e) {
                console.warn("Could not fetch CCP checkpoints from Dataverse:", e);
            }
        }

        // 2. Check IndexedDB
        try {
            const local = await getCCPOPRPCheckpointsForTour(cleanTourId);
            if (local && local.length > 0) return local;
        } catch (_) { }

        return items;
    },

    getSievesMagnetsCheckpoints: async function (tourId: string): Promise<any[]> {
        const cleanTourId = tourId.replace(/[{}]/g, "").trim().toLowerCase();
        let items: any[] = [];

        // 1. Fetch from Dataverse first if online
        if (navigator.onLine) {
            try {
                const tokenResult = await getAccessToken();
                const token = tokenResult?.token;
                if (token) {
                    const apiVersion = "9.2";
                    const query = `?$filter=_cr3ea_qualitytourid_value eq '${cleanTourId}'&$orderby=createdon asc`;
                    const tableCandidates = [
                        TABLES.SIEVES_MAGNETS
                    ];

                    for (const tbl of tableCandidates) {
                        try {
                            const res = await fetch(`${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tbl}${query}`, {
                                headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }
                            });
                            if (res.ok) {
                                const data = await res.json();
                                if (data.value && Array.isArray(data.value)) {
                                    items = data.value;
                                    break;
                                }
                            }
                        } catch (_) { }
                    }

                    if (items.length > 0) {
                        items = items.map((item: any) => {
                            const remarks = item.cr3ea_defectremarks || "";
                            const isNotOkay = item.cr3ea_criteria === "Not Okay";
                            if (remarks.includes(" | Action: ")) {
                                const parts = remarks.split(" | Action: ");
                                const actionAndRev = parts[1] || "";
                                item.cr3ea_actiontaken = actionAndRev.split(" | Re-verified: ")[0] || "";
                                item.cr3ea_deviationstatus = isNotOkay ? "Action Taken" : "Closed";
                            } else if (isNotOkay) {
                                item.cr3ea_deviationstatus = "New";
                                item.cr3ea_actiontaken = "";
                            } else {
                                item.cr3ea_deviationstatus = "Closed";
                            }
                            return item;
                        });

                        // Cache in IndexedDB
                        for (const row of items) {
                            const rowId = row.cr3ea_prod_rajpura_sievesmagnetsid || `sieves_cp_${cleanTourId}_${row.cr3ea_cycle}_${Math.random()}`;
                            try {
                                await putToStore(STORES.SIEVES_MAGNETS_CHECKPOINTS, {
                                    id: rowId,
                                    tourId: cleanTourId,
                                    cycle: row.cr3ea_cycle || "Cycle-1",
                                    data: row,
                                    syncStatus: 'synced',
                                    updatedAt: Date.now()
                                });
                            } catch (_) { }
                        }
                        return items;
                    }
                }
            } catch (e) {
                console.warn("Could not fetch Sieves checkpoints from Dataverse:", e);
            }
        }

        // 2. Check IndexedDB
        try {
            const local = await getSievesMagnetsCheckpointsForTour(cleanTourId);
            if (local && local.length > 0) return local;
        } catch (_) { }

        return items;
    },

    /**
     * Get Tour History
     */
    getTourHistory: async function (): Promise<any[]> {
        let tours: any[] = [];
        if (navigator.onLine) {
            try {
                const tokenResult = await getAccessToken();
                const token = tokenResult?.token;
                if (token) {
                    const apiVersion = "9.2";
                    const query = `?$filter=(cr3ea_plantid eq '14' or cr3ea_plantid eq '2' or cr3ea_plantid eq 'Rajpura')&$orderby=cr3ea_tourstartdate desc&$top=100`;
                    const res = await fetch(`${ENVIRONMENT_URL}/api/data/v${apiVersion}/${TABLES.QUALITY_TOUR}${query}`, {
                        headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const data = await res.json();
                        tours = data.value || [];
                    } else {
                        console.error(`Failed to fetch CCP/OPRP/Sieves tours: ${res.statusText}`);
                    }
                }
            } catch (e) {
                console.warn("Could not fetch CCP/OPRP/Sieves tours live:", e);
            }
        }

        // Cache live records in IndexedDB
        for (const t of tours) {
            const tId = String(t.cr3ea_prod_rajpura_quality_tourid || t.cr3ea_prod_qualitytourid || "").toLowerCase();
            if (tId) {
                try {
                    await putToStore(STORES.CCP_OPRP_SESSIONS, { id: tId, data: t, syncStatus: 'synced', updatedAt: Date.now() });
                } catch (_) {}
            }
        }

        // Merge IndexedDB local tours
        try {
            const localSessions = await getAllFromStore(STORES.CCP_OPRP_SESSIONS);
            if (localSessions && localSessions.length > 0) {
                localSessions.forEach((ls: any) => {
                    const d = ls.data || ls;
                    const id = String(d.cr3ea_prod_rajpura_quality_tourid || d.cr3ea_prod_qualitytourid || "").toLowerCase();
                    if (id && !tours.some(t => String(t.cr3ea_prod_rajpura_quality_tourid || t.cr3ea_prod_qualitytourid || "").toLowerCase() === id)) {
                        tours.push(d);
                    }
                });
            }
        } catch (_) { }

        return tours;
    },

    /**
     * Delete existing items for a cycle before rewrite (pause / resume / restart support)
     */
    cleanChecklistItems: async function (tourId: string, type: "CCP" | "SIEVES", cycleName: string): Promise<boolean> {
        const cleanTourId = tourId.replace(/[{}]/g, "");
        if (navigator.onLine) {
            try {
                const tokenResult = await getAccessToken();
                const token = tokenResult?.token;
                if (token) {
                    const apiVersion = "9.2";
                    const table = type === "CCP" ? TABLES.CCP_OPRP_CHECKS : TABLES.SIEVES_MAGNETS;
                    const idField = type === "CCP" ? "cr3ea_prod_rajpura_ccpoprpid" : "cr3ea_prod_rajpura_sievesmagnetsid";
                    const filter = `?$filter=_cr3ea_qualitytourid_value eq '${cleanTourId}' and cr3ea_cycle eq '${cycleName}'&$select=${idField}`;
                    
                    const res = await fetch(`${ENVIRONMENT_URL}/api/data/v${apiVersion}/${table}${filter}`, {
                        headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const data = await res.json();
                        const items = data.value || [];
                        for (const it of items) {
                            const itemId = it[idField];
                            if (itemId) {
                                await fetch(`${ENVIRONMENT_URL}/api/data/v${apiVersion}/${table}(${itemId})`, {
                                    method: "DELETE",
                                    headers: { "Authorization": `Bearer ${token}` }
                                }).catch(() => {});
                            }
                        }
                    }
                }
            } catch (e) {
                console.warn("Could not clean existing checklist items in Dataverse:", e);
            }
        }
        return true;
    },

    /**
     * Update a single deviation record (Production Action / QA Closure / Escalation)
     */
    updateDeviationStatus: async function (
        recordId: string,
        type: "CCP" | "SIEVES",
        payload: {
            cr3ea_actiontaken?: string;
            cr3ea_deviationstatus?: string;
            cr3ea_defectremarks?: string;
            cr3ea_acceptanceresponse?: string;
            cr3ea_escalated?: string;
        }
    ): Promise<boolean> {
        const cleanId = recordId.replace(/[{}]/g, "");
        if (navigator.onLine) {
            try {
                const tokenResult = await getAccessToken();
                const token = tokenResult?.token;
                if (token) {
                    const apiVersion = "9.2";
                    const table = type === "CCP" ? TABLES.CCP_OPRP_CHECKS : TABLES.SIEVES_MAGNETS;
                    const res = await fetch(`${ENVIRONMENT_URL}/api/data/v${apiVersion}/${table}(${cleanId})`, {
                        method: "PATCH",
                        headers: {
                            "Accept": "application/json",
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        },
                        body: JSON.stringify(payload)
                    });
                    if (res.ok) return true;
                }
            } catch (e) {
                console.warn("Could not update deviation status live:", e);
            }
        }

        // Offline queueing
        try {
            await enqueueSync({
                id: `${Date.now()}_deviation_${cleanId}`,
                type: 'deviation_update',
                payload: { recordId: cleanId, tableType: type, ...payload },
                createdAt: Date.now()
            });
        } catch (_) {}
        return true;
    }
};
